// Dependencias
import DataTable, { Api } from 'datatables.net-bs5';
import Swal from 'sweetalert2';

// Configuraciones
import { generalConfig } from '../config/datatable.config';
import { toastConfiguration } from '../config/sweetAlert.config';
import { ErrorMessagesToast } from '../config/Messages.config';

// Utils
import {
  escapeHtml,
  formatDateTime,
  drawApiKeyStatus,
  drawApiKeyEnvironment,
  drawApiKeyScopes,
  drawApiKeyUser,
  drawApiKeyLastUse,
  drawApiKeyRevoked,
  drawApiKeyActions,
  revokeApiKeyFlow,
  onDomReady
} from '../utils/publicApi';

// Services
import * as publicApiService from '../service/publicApi.service';

// Instancias
const toast = Swal.mixin(toastConfiguration);

// Variables globales
let keysTable: Api<string[]>;
let currentFilters: { status: string; search: string } = { status: 'all', search: '' };

/**
 * Pinta los indicadores generales
 */
async function loadSummary() {
  const setText = (id: string, value: string) => {
    const element = document.querySelector<HTMLElement>(`#${id}`);
    if (element) element.textContent = value;
  };

  try {
    const summary = await publicApiService.fetchApiSummary();
    const numberFormat = new Intl.NumberFormat('es-MX');
    setText('kpi-users-api', numberFormat.format(summary.usersWithApi));
    setText('kpi-active-keys', numberFormat.format(summary.activeKeys));
    setText('kpi-total-keys', numberFormat.format(summary.totalKeys));
    setText('kpi-requests-24h', numberFormat.format(summary.requests24h));
    setText('kpi-errors-24h', numberFormat.format(summary.errors24h));
    setText('kpi-error-rate', `${summary.errorRate24h.toFixed(2)}%`);
  } catch (error) {
    console.error('Error al obtener los indicadores de la API pública:', error);
    toast.fire({ icon: 'error', title: ErrorMessagesToast.ERROR });
  }
}

/**
 * Formatea las keys como filas de la tabla
 */
const formatKeys = (keys: publicApiService.ApiKey[]) =>
  keys.map((key) => [
    `${Number(key.id)}`,
    drawApiKeyUser(key),
    escapeHtml(key.name),
    `<code>${escapeHtml(key.key_prefix)}</code>`,
    drawApiKeyEnvironment(key.environment),
    drawApiKeyScopes(key.scopes),
    drawApiKeyStatus(key.status),
    formatDateTime(key.created_at),
    drawApiKeyLastUse(key),
    formatDateTime(key.expires_at, '<span class="text-white-50">Nunca</span>'),
    drawApiKeyRevoked(key),
    drawApiKeyActions(key)
  ]);

export function main() {
  onDomReady(() => {
    const tableElement = document.querySelector<HTMLTableElement>('#api-keys-list');
    const formElement = document.querySelector<HTMLFormElement>('#form-search-api-keys');
    if (!tableElement || !formElement) return;

    loadSummary();

    // Tabla con paginación del lado del servidor (LIMIT/OFFSET en el server del ERP)
    keysTable = new DataTable(tableElement, Object.assign({}, generalConfig, {
      serverSide: true,
      processing: true,
      ordering: false,
      ajax: async(data: { draw: number; start: number; length: number }, callback: (response: unknown) => void) => {
        try {
          const limit = data.length > 0 ? data.length : 10;
          const page = Math.floor(data.start / limit) + 1;
          const response = await publicApiService.fetchApiKeys({
            page,
            limit,
            status: currentFilters.status,
            search: currentFilters.search
          });

          callback({
            draw: data.draw,
            recordsTotal: response.pagination.totalItems,
            recordsFiltered: response.pagination.totalItems,
            data: formatKeys(response.keys)
          });
        } catch (error) {
          console.error('Error al obtener las API keys:', error);
          toast.fire({ icon: 'error', title: ErrorMessagesToast.ERROR });
          callback({ draw: data.draw, recordsTotal: 0, recordsFiltered: 0, data: [] });
        }
      }
    }));

    // Filtros (estado + búsqueda)
    formElement.addEventListener('submit', (e) => {
      e.preventDefault();
      const json = Object.fromEntries(new FormData(formElement)) as { status?: string; search?: string };
      currentFilters = {
        status: json.status || 'all',
        search: (json.search || '').trim()
      };
      // reload() reinicia la paginación a la primera página y vuelve a consultar
      keysTable.ajax.reload();
    });

    // Acción de revocar (delegación de eventos)
    tableElement.addEventListener('click', async(e) => {
      const button = (e.target as HTMLElement).closest<HTMLButtonElement>('.btn-revoke-api-key');
      if (!button) return;

      const revoked = await revokeApiKeyFlow({
        id: Number(button.dataset.keyId),
        name: button.dataset.keyName || '',
        prefix: button.dataset.keyPrefix || ''
      });

      if (revoked) {
        keysTable.ajax.reload(undefined, false);
        loadSummary();
      }
    });
  });
}
